/** All UI text (nl-BE). Components never hardcode user-facing strings. */
export const nl = {
  app: {
    name: "Stagevijver",
    tagline: "Check een bedrijf voor je solliciteert.",
    yearLabel: "SNB · 2026–27",
    breadcrumbRoot: "Werkplekleren 2026–27",
  },
  nav: {
    main: "Hoofdmenu",
    breadcrumb: "Kruimelpad",
    admin: "Beheer",
    status: "Status",
    companies: "Bedrijven",
    map: "Kaart",
    activity: "Activiteit",
    followed: "Gevolgd",
    profile: "Profiel",
    moderation: "Moderatie",
    stats: "Statistieken",
    inviteCodes: "Klascodes",
    newReport: "Nieuwe melding",
    search: "Check een bedrijf…",
    view: "Weergave",
    list: "Lijst",
    unread: (count: number) => `${count} nieuw`,
    toModerate: (count: number) => `${count} te modereren`,
  },
  theme: {
    system: "Systeem",
    light: "Licht",
    dark: "Donker",
    toggle: (current: string, next: string) => `Thema: ${current}. Wissel naar ${next}.`,
  },
  common: {
    close: "Sluiten",
    skipToContent: "Naar de inhoud",
    undo: "Ongedaan maken",
    loading: "Laden…",
    retry: "Opnieuw proberen",
  },
  screens: {
    privacy: "Privacy",
    companies: "Bedrijven",
    map: "Kaart",
    activity: "Activiteit",
    followed: "Gevolgd",
    search: "Zoeken",
    report: "Nieuwe melding",
    profile: "Profiel",
    onboarding: "Welkom",
    register: "Account maken",
    login: "Inloggen",
    recover: "Toegang herstellen",
    moderation: "Moderatie",
    stats: "Statistieken",
    inviteCodes: "Klascodes",
    launch: "Stagevijver",
  },
  status: {
    kans: "Nog plaats",
    tip: "Tip",
    gesprek: "In gesprek",
    volzet: "Volzet",
    nee: "Nee",
  },
  advice: {
    label: "Advies voor jou",
  },
  dev: {
    componentsTitle: "Componenten",
    componentsIntro: "Alle varianten van de kerncomponenten (enkel zichtbaar in de dev-build).",
  },
  launch: {
    navLabel: "Lanceerpagina",
    navHow: "Hoe het werkt",
    navRules: "Spelregels",
    navFaq: "FAQ",
    login: "Inloggen",
    kicker: "Werkplekleren 2026–27 · SNB",
    heroTitle: "Check voor je solliciteert.",
    heroLead:
      "Zie in één oogopslag welke bedrijven al nee zeiden, waar de plaats al ingevuld is en wie er nu in gesprek is. Door en voor studenten Systeem- en Netwerkbeheer.",
    ctaRegister: "Maak een account",
    ctaHow: "Hoe werkt het?",
    heroNote: "Gratis · enkel met je @student.thomasmore.be-adres en een klascode",
    previewLabel: "Voorbeeld met fictieve bedrijven",
    previewList: "Voorbeeldlijst",
    previewProfile: "Profiel: Systemen",
    previewForYou: "Voor jou",
    previewDistance: (km: number) => `${km} km van Mol`,
    // Fictional companies, only for the illustration on this page.
    previewCompanies: [
      {
        id: "voorbeeld-netvalley",
        name: "Netvalley bv",
        status: "kans",
        place: "Turnhout",
        km: 19,
        advice: "Claim nu",
        title: "Hier kan je terecht",
        text: "Nog 1 van 2 plaatsen vrij. Met een claim weet de rest dat jij 21 dagen aan zet bent.",
        cta: "Claim voor 21 dagen",
      },
      {
        id: "voorbeeld-kempen",
        name: "Kempen Logistics NV",
        status: "nee",
        place: "Geel",
        km: 10,
        advice: "Sla over",
        title: "Sla dit bedrijf over",
        text: "IT extern beheerd door Nexora ICT. Drie studenten kregen dezelfde nee. Probeer Nexora ICT.",
        cta: "Bekijk Nexora ICT",
      },
      {
        id: "voorbeeld-peeters",
        name: "Peeters Metaalwerken",
        status: "gesprek",
        place: "Lommel",
        km: 14,
        advice: "Wachtlijst",
        title: "Een medestudent is aan zet",
        text: "Een medestudent is in gesprek tot 11 november. Komt het bedrijf vrij, dan hoor jij het eerst.",
        cta: "Zet me op de wachtlijst",
      },
      {
        id: "voorbeeld-nexora",
        name: "Nexora ICT",
        status: "tip",
        place: "Herentals",
        km: 20,
        advice: "Wees de eerste",
        title: "Nog onontgonnen",
        text: "MSP van Kempen Logistics. Nog door niemand gecontacteerd, en ze beheren servers.",
        cta: "Claim voor 21 dagen",
      },
    ],
    howKicker: "Hoe het werkt",
    howTitle: "Drie stappen, minder brieven voor niets.",
    howLead: "Elke melding maakt de vijver een beetje helderder voor wie na jou komt.",
    steps: [
      {
        title: "Check een bedrijf",
        text: "Zoek op naam of gemeente. Je ziet meteen of iemand al aanklopte, wat het antwoord was en waarom.",
      },
      {
        title: "Claim voor je solliciteert",
        text: "Een claim geldt 21 dagen, je hebt er maximaal 2 tegelijk. Zo weet iedereen dat jij in gesprek bent.",
      },
      {
        title: "Meld hoe het afliep",
        text: "Ook een nee helpt. Met de reden erbij weet de volgende of het voor zijn profiel wél kan.",
      },
    ],
    featuresTitle: "Meer dan een lijst met namen.",
    featuresLead:
      "Stagevijver kijkt mee met jouw profiel en je startpunt, zodat je tijd steekt in bedrijven waar het kan.",
    features: [
      {
        title: "Advies op maat van je profiel",
        text: "Zei een bedrijf nee voor netwerken maar niet voor systemen? Dan zie jij \"Kans voor jou\" in plaats van \"Sla over\".",
      },
      {
        title: "Wachtlijst als iemand anders aan zet is",
        text: "Zet je op de wachtlijst. Sluit de andere student af of loopt de claim af, dan krijg jij als eerste een seintje.",
      },
      {
        title: "Volg de lijn",
        text: "\"IT extern beheerd\" is geen dood spoor. De MSP achter dat bedrijf komt als nieuwe kans in de lijst.",
      },
      {
        title: "Kaart met afstand vanaf je gemeente",
        text: "Kies je startpunt en zie per bedrijf hoe ver het is. Ook waar nog niemand gevist heeft.",
      },
    ],
    rulesKicker: "Spelregels",
    rulesTitle: "Het werkt enkel als iedereen meedoet.",
    rules: [
      {
        title: "Geen namen of gegevens van contactpersonen",
        text: "Meld het bedrijf, niet de persoon. Dus ook geen e-mailadressen of gsm-nummers.",
      },
      {
        title: "Zakelijk melden, geen oordelen",
        text: "Wat zeiden ze en waarom. Of een bedrijf \"tof\" of \"slecht\" is, hoort hier niet.",
      },
      {
        title: "Claim enkel als je echt solliciteert",
        text: "Een claim houdt anderen tegen. Reserveer dus niet \"voor het geval dat\".",
      },
      {
        title: "Sluit je claim af, ook bij een nee",
        text: "Twee klikken. Daarmee geef je het bedrijf vrij en weet de volgende waar hij staat.",
      },
    ],
    privacyTitle: "Privacy, kort en eerlijk",
    privacyKeepTitle: "Wat we bewaren",
    privacyKeep: [
      "Je schoolmail",
      "Je gemeente, voor de afstand",
      "Je meldingen (anoniem tonen kan)",
    ],
    privacyNeverTitle: "Wat nooit",
    privacyNever: ["Contactpersonen", "Je adres", "Je cijfers"],
    privacyMore: "Lees alles over privacy",
    privacyNote:
      "Je kan je account altijd verwijderen. Je meldingen blijven dan enkel anoniem staan, zodat de info voor anderen bewaard blijft.",
    faqKicker: "FAQ",
    faqTitle: "Vragen die we al kregen",
    faqLead: "Staat je vraag er niet bij? Stel ze gerust in de klasgroep.",
    faq: [
      {
        q: "Is dit een officieel platform van Thomas More?",
        a: "Nee. Stagevijver is een initiatief van studenten Systeem- en Netwerkbeheer. We informeren de stagecoördinator over hoe het werkt en wat erop komt, maar de afspraken van school over werkplekleren gaan altijd voor.",
      },
      {
        q: "Hoe maak ik een account?",
        a: "Met je schoolmail en de klascode van je klasgroep. Die krijg je van je klasgenoten of de stagecoördinator. Daarna kies je een passkey (Face ID, vingerafdruk of Windows Hello) of een wachtwoord. We sturen geen mails om in te loggen.",
      },
      {
        q: "Wie ziet mijn meldingen?",
        a: "Enkel ingelogde studenten met een @student.thomasmore.be-adres. Meld je anoniem (standaard), dan zien andere studenten je initialen niet. Moderatoren kunnen bij misbruik zien van welk account een melding komt.",
      },
      {
        q: "Wat als een melding niet klopt?",
        a: "Meld het bij de melding en zeg kort wat er veranderd is. Een nieuwe melding van iemand die zelf contact had, vervangt de oude status. Twijfel je, dan kijkt een moderator mee.",
      },
      {
        q: "Waarom maximaal 2 claims?",
        a: "Zodat niemand de beste bedrijven tegelijk vastzet. Twee lopende sollicitaties is genoeg om mee bezig te zijn. Sluit je er een af, dan kan je meteen een nieuwe nemen, en wie op de wachtlijst staat krijgt een seintje.",
      },
      {
        q: "Kost het iets?",
        a: "Nee, het is gratis. Geen advertenties, en we geven of verkopen niets door aan bedrijven of derden.",
      },
    ],
    finalTitle: "Hoe minder brieven in de prullenmand, hoe beter.",
    finalText:
      "Een account maken duurt een minuut: je schoolmail, de klascode van je klasgroep en een passkey of wachtwoord.",
    footer: "Initiatief van studenten SNB · geen officieel platform van Thomas More",
  },
  companies: {
    mobileTitle: "Waar kan je nog vissen?",
    searchLabel: "Zoek in de lijst",
    searchPlaceholder: "Naam of gemeente",
    filters: "Filters",
    filterAll: "Alle",
    filterForMe: "Voor jou",
    listLabel: "Bedrijven",
    count: (n: number) => (n === 1 ? "1 bedrijf" : `${n} bedrijven`),
    loadMore: "Meer laden",
    loadError: "De bedrijven konden niet geladen worden.",
    empty: "Nog geen bedrijven.",
    emptyFilter: "Niets gevonden met deze filter of zoekterm.",
    clearFilters: "Alles tonen",
    selectHint: "Kies een bedrijf om het advies te zien.",
    detailError: "Dit bedrijf kon niet geladen worden.",
    notFound: "Dit bedrijf bestaat niet (meer).",
    keyboardHint: "Blader met de pijltjestoetsen.",
    distance: (km: number) => `${km.toLocaleString("nl-BE", { maximumFractionDigits: km < 10 ? 1 : 0 })} km`,
    tags: { myClaim: "Jouw claim", waitlist: "Wachtlijst", followed: "Gevolgd" },
    reasons: {
      it_extern: "IT extern beheerd",
      geen_stages: "Neemt geen stagiairs",
      geen_begeleider: "Geen begeleider",
      al_volzet: "Al volzet",
      anders: "Andere reden",
    },
    outcomes: {
      nee: "Nee",
      solliciteer: "Solliciteert",
      ja_voor_mij: "Kreeg ja",
      zoeken_nog: "Zoeken nog stagiairs",
      geen_reactie: "Geen reactie",
      tip: "Tip",
    },
    channels: { telefoon: "telefonisch", mail: "per mail", ter_plaatse: "ter plaatse" },
    adviceTitles: {
      skip: "Sla dit bedrijf over",
      maybe_for_you: "Mogelijk wel voor jou",
      available: "Hier kan je terecht",
      unexplored: "Nog onontgonnen",
      own_claim: "Jouw claim loopt nog",
      other_claim: "Een medestudent is aan zet",
      full: "Alle plaatsen zijn ingevuld",
    },
    ownClaimDays: (days: number) => (days === 1 ? "Jouw claim loopt nog 1 dag" : `Jouw claim loopt nog ${days} dagen`),
    adviceTexts: {
      skip: "Dit bedrijf zei nee, ook voor jouw profiel. Je tijd is beter besteed aan een ander bedrijf.",
      maybe_for_you: "Het nee gold voor andere profielen. Voor jouw profiel is er misschien wel plaats.",
      available: "Dit bedrijf zei ja en heeft nog vrije plaatsen.",
      unexplored: "Nog niemand contacteerde dit bedrijf. Wees de eerste en laat weten hoe het ging.",
      own_claim: "Laat weten hoe het liep, of verleng je claim één keer.",
      other_claim: "Een medestudent solliciteert hier. Zet je op de wachtlijst en je hoort het als het vrijkomt.",
      full: "Alle plaatsen van dit academiejaar zijn ingevuld. Volgend jaar kan het opnieuw.",
    },
    adviceLabels: {
      sla_over: "Sla over",
      kans_voor_jou: "Kans voor jou",
      claim_nu: "Claim nu",
      wees_de_eerste: "Wees de eerste",
      jouw_claim: "Jouw claim",
      wachtlijst: "Wachtlijst",
      volgend_jaar: "Volgend jaar",
    },
    actions: {
      claim: "Claim voor 21 dagen",
      close_yes: "Ik kreeg ja",
      close_no: "Ik kreeg nee",
      extend: "Verleng 7 dagen",
      join_waitlist: "Zet me op de wachtlijst",
      leave_waitlist: "Haal me van de wachtlijst",
      remind_next_year: "Herinner me in september",
      cancel_reminder: "Herinnering annuleren",
      report: "Toch contact gehad? Meld het",
    },
    disabled: {
      max_claims_reached: "Je hebt al 2 actieve claims. Sluit er eerst een af.",
      waitlist_priority: "Een medestudent van de wachtlijst heeft nog voorrang.",
    },
    comingSoon: "Claimen en de wachtlijst vanuit de fiche komen binnenkort.",
    waitlistPosition: (n: number) => `Je staat ${n === 1 ? "eerst" : `op plaats ${n}`} op de wachtlijst.`,
    follow: "Volgen",
    following: "Gevolgd",
    followed: (name: string) => `Je volgt ${name}`,
    unfollowed: (name: string) => `Je volgt ${name} niet meer`,
    kbo: (number: string) => `KBO ${number}`,
    soleProprietorship: "Eenmanszaak",
    facts: "Feiten",
    spotsFree: "Vrije plaatsen",
    spotsValue: (free: number | null, total: number | null) =>
      free === null ? "Onbekend" : total === null ? String(free) : `${free} van ${total}`,
    reportsThisYear: "Meldingen dit jaar",
    itManagement: "IT-beheer",
    it: { intern: "Intern", extern: "Extern", onbekend: "Onbekend" },
    noResponse: (n: number) => `${n}× geen reactie per mail: bel liever.`,
    followTheLine: (msp: string) => `Volg de lijn naar ${msp}`,
    followTheLineText: "De IT wordt extern beheerd. Misschien zoekt de beheerder wel stagiairs.",
    history: "Geschiedenis",
    historyEmpty: "Nog geen meldingen.",
    previousYear: (year: string) => `Academiejaar ${year}`,
    by: (initials: string) => `door ${initials}`,
    anonymous: "anoniem",
    tips: "Tips van medestudenten",
    back: "Terug naar de lijst",
    report: "Meld contact",
  },
  prefs: {
    profilesLabel: "Welke richting(en) volg je?",
    profilesHelp: "Kies er minstens één. Zo zien we welk nee voor jou geldt.",
    profilesRequired: "Kies minstens één profiel.",
    profiles: {
      netwerk: { label: "Netwerken", text: "Switching, routing, wifi" },
      systemen: { label: "Systemen", text: "Servers, Windows, Linux" },
      security: { label: "Security", text: "Firewalls, SOC, hardening" },
      cloud: { label: "Cloud en M365", text: "Azure, Microsoft 365, Intune" },
      helpdesk: { label: "Helpdesk", text: "Support en werkplekbeheer" },
      onbekend: { label: "Weet ik nog niet", text: "Toon alles zonder filter" },
    },
    municipality: "Vanwaar vertrek je?",
    municipalityHelp: "Gemeente of postcode. We gebruiken enkel het middelpunt, nooit je adres.",
    municipalityPlaceholder: "bv. Geel of 2440",
    municipalityNone: "Geen gemeente gevonden.",
    municipalitySearching: "Zoeken…",
    municipalityClear: "Wissen",
    municipalityCredit: "Gemeenten en postcodes: GeoNames (CC BY 4.0)",
    distance: "Hoe ver wil je reizen?",
    distanceOptions: { 10: "10 km", 25: "25 km", 50: "50 km", 100: "100 km" },
    distanceAny: "Maakt niet uit",
    transport: "Hoe kom je er?",
    transports: { auto: "Auto", ov: "Openbaar vervoer", fiets: "Fiets" },
    notifyLabel: "Wat wil je horen?",
    notifyHelp: "Meldingen komen in de app, bij Activiteit.",
    notify: {
      claim: { label: "Mijn claims", text: "Herinnering op dag 14 en als een claim vervalt" },
      wait: { label: "Wachtlijst", text: "Als een bedrijf vrijkomt en jij voorrang hebt" },
      follow: { label: "Gevolgde bedrijven", text: "Nieuwe meldingen en je herinneringen" },
      digest: { label: "Wekelijks overzicht", text: "Op maandag, enkel per mail" },
      email: { label: "Ook per mail", text: "Naar je schoolmail, als Stagevijver mail kan versturen" },
    },
  },
  onboarding: {
    stepOf: (step: number, total: number) => `Stap ${step} van ${total}`,
    titles: ["Wat studeer je?", "Waar zoek je?", "Meldingen"],
    lead: "Zo krijg je advies op maat. Je kan dit later aanpassen bij Profiel.",
    next: "Verder",
    back: "Terug",
    finish: "Klaar",
    saving: "Opslaan…",
  },
  profile: {
    preferences: "Voorkeuren",
    account: "Account",
    email: "Schoolmail",
    initials: "Initialen",
    initialsHelp: "Zo zien anderen je bij een melding (tenzij je anoniem meldt).",
    save: "Opslaan",
    saved: "Voorkeuren opgeslagen",
    recoveryTitle: "Herstelcodes",
    recoveryLeft: (n: number) => (n === 1 ? "Nog 1 ongebruikte herstelcode." : `Nog ${n} ongebruikte herstelcodes.`),
    recoveryNew: "Nieuwe herstelcodes maken",
    recoveryNewText: "Je oude codes werken dan niet meer.",
    recoveryDone: "Klaar",
    logout: "Uitloggen",
    delete: {
      title: "Account verwijderen",
      lead: "Je account, je wachtwoord, je passkeys en je sessies verdwijnen. Je meldingen blijven anoniem staan, zodat de info voor anderen bewaard blijft.",
      open: "Account verwijderen…",
      privacyLink: "Wat we bewaren",
      confirmTitle: "Account definitief verwijderen?",
      confirmText: "Dit kan je niet ongedaan maken.",
      what: [
        "Je schoolmail, initialen, voorkeuren, wachtwoord, passkeys en herstelcodes worden gewist.",
        "Lopende claims stoppen; wie op de wachtlijst staat, krijgt voorrang.",
        "Je meldingen blijven staan, maar anoniem en zonder koppeling naar jou.",
        "Opnieuw beginnen kan later met een nieuwe klascode.",
      ],
      typeEmail: (email: string) => `Typ ${email} om te bevestigen`,
      cancel: "Annuleren",
      confirm: "Definitief verwijderen",
    },
    loadError: "Je profiel kon niet geladen worden.",
  },
  report: {
    title: "Nieuwe melding",
    lead: "Laat weten hoe het contact liep. Zo stuurt niemand nog een brief naar een bedrijf dat al nee zei.",
    company: "Bedrijf",
    companyPlaceholder: "Zoek op naam of gemeente",
    companyNone: "Geen bedrijf gevonden. Toevoegen kan binnenkort via Zoeken.",
    companyChange: "Ander bedrijf",
    outcome: "Wat is er gebeurd?",
    outcomes: {
      nee: { label: "Ze zeiden nee", text: "Geen stage mogelijk" },
      solliciteer: { label: "Ik solliciteer", text: "Claim het bedrijf 21 dagen" },
      ja_voor_mij: { label: "Ik kreeg ja", text: "Ik heb hier een stageplaats" },
      zoeken_nog: { label: "Ze zoeken nog stagiairs", text: "Er zijn nog plaatsen vrij" },
      geen_reactie: { label: "Geen reactie", text: "Ik hoorde niets terug" },
      tip: { label: "Enkel een tip", text: "Ik had zelf geen contact" },
    },
    reason: "Waarom niet?",
    reasons: {
      it_extern: "IT wordt extern beheerd",
      geen_stages: "Ze nemen geen stagiairs",
      geen_begeleider: "Geen begeleider beschikbaar",
      al_volzet: "Al volzet (bv. via een andere school)",
      anders: "Andere reden",
    },
    reasonNote: "Korte toelichting",
    reasonNoteHelp: "Zonder namen van personen.",
    scopeQuestion: "Geldt dit nee voor elk profiel?",
    scopeAll: "Ja, voor iedereen",
    scopeSome: "Nee, enkel voor…",
    mspName: "Wie beheert hun IT? (optioneel)",
    mspHelp: "Naam van het IT-bedrijf. Misschien zoeken zij wel stagiairs.",
    spots: "Plaatsen (optioneel)",
    spotsTotal: "Aantal plaatsen",
    spotsFilled: "Waarvan al ingevuld",
    more: "Meer details",
    less: "Minder details",
    channel: "Hoe nam je contact op?",
    tip: "Tip voor medestudenten",
    tipHelp: "Bv. wie je best belt of wat ze vragen. Geen namen, mailadressen of telefoonnummers.",
    tipCount: (n: number, max: number) => `${n}/${max}`,
    anonymous: "Anoniem melden",
    anonymousHelp: "Medestudenten zien je initialen niet. Moderatoren wel.",
    submit: "Melding versturen",
    submitting: "Versturen…",
    personalTitle: "Dit lijkt persoonsgegevens te bevatten",
    personalText: "Haal namen, mailadressen, telefoonnummers en links weg. Weet je zeker dat het er geen zijn? Verstuur dan toch; een moderator kijkt het na.",
    personalKinds: { email: "e-mailadres", phone: "telefoonnummer", url: "link", name: "naam" },
    personalConfirm: "Toch versturen",
    doneTitles: {
      nee: "Bedankt, dit bespaart brieven",
      solliciteer: "Succes met je sollicitatie",
      ja_voor_mij: "Proficiat met je stageplaats!",
      zoeken_nog: "Bedankt voor de tip",
      geen_reactie: "Genoteerd",
      tip: "Bedankt voor de tip",
    },
    doneTexts: {
      nee: "Medestudenten zien meteen dat ze dit bedrijf kunnen overslaan.",
      solliciteer: "Je claim loopt 21 dagen. Laat weten hoe het liep, dan komt het bedrijf weer vrij als het nee wordt.",
      ja_voor_mij: "Je plaats is ingevuld. Als er nog plaatsen zijn, zien anderen dat.",
      zoeken_nog: "Medestudenten zien dat er nog plaats is.",
      geen_reactie: "Geen reactie verandert de status niet, maar we tellen het wel.",
      tip: "Je tip staat bij het bedrijf.",
    },
    undo: "Ongedaan maken",
    undone: "Melding ongedaan gemaakt",
    sent: "Melding verstuurd",
    toCompany: "Naar het bedrijf",
    another: "Nog een melding",
  },
  claims: {
    sidebarTitle: (n: number, max: number) => `Jouw claims (${n}/${max})`,
    none: "Nog geen claims. Claim een bedrijf voor je solliciteert.",
    daysLeft: (days: number) => (days === 1 ? "nog 1 dag" : `nog ${days} dagen`),
    claimed: (name: string, until: string) => `${name} is van jou tot ${until}`,
    unclaimed: "Claim ongedaan gemaakt",
    extended: (until: string) => `Claim verlengd tot ${until}`,
    joined: "Je staat op de wachtlijst",
    left: "Je staat niet meer op de wachtlijst",
    reminderOn: "We herinneren je in september",
    reminderOff: "Herinnering geannuleerd",
    closeYesTitle: "Je kreeg ja: proficiat!",
    closeYesText: "Weet je hoeveel stageplaatsen er zijn? Dan zien anderen of er nog plaats is.",
    closeNoTitle: "Je kreeg nee",
    closeNoText: "Waarom? Zo sparen medestudenten zich een brief.",
    closeWithdraw: "Ik solliciteer toch niet",
    closeWithdrawText: "Je claim stopt zonder melding; het bedrijf komt vrij.",
    closeSubmit: "Claim afsluiten",
    closedYes: "Claim afgesloten met ja",
    closedNo: "Claim afgesloten met nee",
    withdrawn: "Claim ingetrokken",
    cancel: "Annuleren",
  },
  activity: {
    tabs: { all: "Alles", for_me: "Voor mij", followed: "Gevolgd", claims: "Mijn claims" },
    tabsLabel: "Soort meldingen",
    empty: "Nog niets te melden.",
    emptyTab: "Niets in deze tab.",
    loadError: "De activiteit kon niet geladen worden.",
    loadMore: "Meer laden",
    markAll: "Alles gelezen",
    unread: "Nieuw",
    today: "Vandaag",
    yesterday: "Gisteren",
    preferences: "Meldingsvoorkeuren",
    saved: "Meldingsvoorkeuren opgeslagen",
    claimNow: "Claim nu",
    texts: {
      claim_reminder: (company: string) => `Je claim op ${company} loopt bijna af. Hoe liep het?`,
      claim_expired: (company: string) => `Je claim op ${company} is verlopen.`,
      waitlist_priority: (company: string, until: string) =>
        `${company} kwam vrij. Jij hebt voorrang tot ${until}.`,
      followed_report: (company: string) => `Nieuwe melding over ${company}.`,
      reminder_next_year: (company: string) => `Het nieuwe academiejaar is begonnen: denk aan ${company}.`,
    },
    unknown: "Nieuwe melding.",
  },
  map: {
    label: "Kaart met bedrijven",
    listTitle: "Op de kaart",
    listCount: (n: number) => (n === 1 ? "1 bedrijf in beeld" : `${n} bedrijven in beeld`),
    showList: (n: number) => `Toon lijst (${n})`,
    nearFilter: (km: number | null) => (km === null ? "In de buurt" : `≤ ${km} km`),
    nearNeedsHome: "Kies eerst je startgemeente in je profiel om op afstand te filteren.",
    onlyKans: "Enkel kansen",
    fished: "Waar al gevist werd",
    fishedHelp: "Toon ook bedrijven die nee zeiden of volzet zijn.",
    open: "Bekijk bedrijf",
    close: "Sluiten",
    emptyView: "Geen bedrijven in dit stuk van de kaart. Zoom uit of verschuif de kaart.",
    whiteSpots: "Witte vlek: hier meldde nog niemand iets. Wees de eerste!",
    noTiles: "De kaart is nog niet ingesteld. Hieronder staan de bedrijven als lijst.",
    noCoordinates: (n: number) =>
      n === 1 ? "1 bedrijf zonder locatie staat niet op de kaart." : `${n} bedrijven zonder locatie staan niet op de kaart.`,
    loadError: "De kaart kon niet geladen worden.",
    pin: (name: string, status: string) => `${name}, ${status}`,
  },
  search: {
    title: "Check een bedrijf",
    label: "Bedrijf zoeken",
    placeholder: "Naam, gemeente of KBO-nummer",
    hint: "Typ minstens 2 tekens. Pijltjes om te kiezen, Enter om te openen.",
    results: "In Stagevijver",
    none: (q: string) => `Niets gevonden voor "${q}".`,
    searching: "Zoeken…",
    error: "Zoeken lukte niet. Probeer opnieuw.",
    open: "Open fiche",
    add: "Bedrijf toevoegen",
    addHint: "Staat het er niet bij? Voeg het toe, dan zien anderen het ook.",
  },
  addCompany: {
    title: "Bedrijf toevoegen",
    name: "Naam van het bedrijf",
    namePlaceholder: "bv. Kempen Data bv",
    municipality: "Gemeente",
    municipalityHelp: "Gemeente of postcode van de vestiging.",
    kbo: "Ondernemingsnummer (optioneel)",
    kboHelp: "Maakt dubbels uitgesloten. Zonder nummer kijkt een moderator het na.",
    kboLink: "Zoek het nummer op in de KBO Public Search",
    sole: "Dit is een eenmanszaak",
    soleHelp: "Dan tonen we enkel de handelsnaam en de gemeente.",
    submit: "Toevoegen",
    submitting: "Toevoegen…",
    exists: "Dit bedrijf staat al in de lijst.",
    duplicate: "Er staat al een bedrijf met deze naam in deze gemeente.",
    openExisting: "Open dat bedrijf",
    addAnyway: "Het is een ander bedrijf, toch toevoegen",
    added: (name: string) => `${name} staat nu in de lijst`,
    nextTitle: "Wat nu?",
    claim: "Ik ga solliciteren (claim)",
    tip: "Enkel een tip geven",
    contact: "Ik had al contact",
    later: "Later",
    review: "Zonder ondernemingsnummer kijkt een moderator het bedrijf nog na.",
  },
  auth: {
    footer: "Initiatief van studenten SNB · geen officieel platform van Thomas More",
    heroTitle: "Check voor je solliciteert.",
    heroLead: "Zie waar medestudenten al aanklopten. Geen dubbel werk, geen verspilde motivatiebrieven.",
    email: "Schoolmail",
    emailPlaceholder: "r0123456@student.thomasmore.be",
    emailHelp: "Enkel adressen op @student.thomasmore.be.",
    password: "Wachtwoord",
    newPassword: "Nieuw wachtwoord",
    passwordHelp: "Minstens 12 tekens. Een korte zin werkt goed, bv. “stage in geel 2027”.",
    show: "Toon",
    hide: "Verberg",
    required: "Vul dit veld in.",
    working: "Even geduld…",
    login: {
      title: "Inloggen",
      passkey: "Inloggen met passkey",
      passkeyHelp: "Met Face ID, vingerafdruk of Windows Hello.",
      or: "of met je wachtwoord",
      submit: "Inloggen",
      lost: "Geen toegang meer?",
      noAccount: "Nog geen account?",
      register: "Maak een account",
    },
    register: {
      title: "Account maken",
      stepOf: (step: number, total: number) => `Stap ${step} van ${total}`,
      next: "Verder",
      back: "Terug",
      inviteCode: "Klascode",
      inviteCodePlaceholder: "ABCD-EFGH",
      inviteCodeHelp: "Vraag de code aan je klasgenoten of de stagecoördinator.",
      methodTitle: "Hoe wil je inloggen?",
      passkeyTitle: "Maak een passkey",
      passkeyRecommended: "Aanbevolen",
      passkeyText: "Inloggen met Face ID, vingerafdruk of Windows Hello. Niets te onthouden.",
      passkeyButton: "Maak een passkey",
      passwordTitle: "Kies een wachtwoord",
      passwordButton: "Account maken",
      noPasskeySupport: "Deze browser ondersteunt geen passkeys. Kies een wachtwoord.",
      haveAccount: "Al een account?",
    },
    codes: {
      title: "Bewaar je herstelcodes",
      text: "Kwijt je je passkey of wachtwoord, dan kom je met een van deze codes weer binnen. Elke code werkt één keer. We tonen ze maar één keer.",
      copy: "Kopieer",
      copied: "Gekopieerd",
      download: "Download als tekstbestand",
      confirm: "Ik heb mijn herstelcodes veilig bewaard",
      continue: "Verder",
      fileName: "stagevijver-herstelcodes.txt",
      fileHeader: "Herstelcodes voor Stagevijver. Elke code werkt één keer.",
    },
    recover: {
      title: "Toegang herstellen",
      text: "Gebruik een van je herstelcodes. Daarna worden al je andere sessies afgemeld.",
      code: "Herstelcode",
      codePlaceholder: "xxxxx-xxxxx",
      newPasswordHelp: "Optioneel. Laat leeg als je straks een nieuwe passkey maakt.",
      submit: "Herstel toegang",
      noCode: "Geen herstelcode meer? Vraag een moderator om een eenmalige code.",
      backToLogin: "Terug naar inloggen",
    },
  },
  placeholder: {
    title: "Stagevijver is in aanbouw",
    body: "Dit scherm komt er binnenkort aan.",
  },
  admin: {
    onlyModerators: "Enkel voor moderatoren",
    onlyModeratorsText: "Dit deel van Stagevijver is enkel voor moderatoren.",
    invites: {
      lead: "Een klascode is nodig om een account te maken. Maak er een per klasgroep of per semester en trek een gelekte code meteen in.",
      newTitle: "Nieuwe klascode",
      label: "Label",
      labelPlaceholder: "bv. SNB 2A 2026-2027",
      expires: "Vervalt op (optioneel)",
      expiresHelp: "Tot en met deze dag. Leeg = verloopt niet.",
      maxUses: "Maximum aantal accounts (optioneel)",
      maxUsesHelp: "Leeg = onbeperkt.",
      create: "Code maken",
      listTitle: "Klascodes",
      empty: "Nog geen klascodes.",
      loadError: "De klascodes konden niet geladen worden.",
      used: (uses: number, max: number | null) => (max === null ? `${uses} gebruikt` : `${uses} van ${max} gebruikt`),
      expiresOn: (date: string) => `vervalt ${date}`,
      noExpiry: "verloopt niet",
      states: {
        active: "Actief",
        expired: "Verlopen",
        exhausted: "Opgebruikt",
        revoked: "Ingetrokken",
      },
      revoke: "Intrekken",
      revokeTitle: "Klascode intrekken?",
      revokeText: (label: string) => `Met "${label}" kan niemand nog een account maken. Bestaande accounts blijven werken.`,
      revokeConfirm: "Intrekken",
      cancel: "Annuleren",
      revoked: "Klascode ingetrokken",
      createdTitle: "Deel deze klascode",
      createdText: "Je ziet deze code maar één keer. Deel ze met de klasgroep, bv. via het klaskanaal.",
      copy: "Kopiëren",
      copied: "Gekopieerd",
      close: "Klaar",
    },
    users: {
      title: "Account opzoeken",
      lead: "Enkel op het volledige schoolmailadres. Elke opzoeking komt in de auditlog.",
      email: "Schoolmail",
      find: "Opzoeken",
      created: (date: string) => `account sinds ${date}`,
      active: "Actief",
      deactivated: "Gedeactiveerd",
      moderator: "Moderator",
      recoveryCode: "Herstelcode maken",
      recoveryTitle: "Herstelcode",
      recoveryText: (until: string) =>
        `Geef deze code persoonlijk door, nooit via mail of chat. Ze werkt één keer en tot ${until}.`,
      deactivate: "Deactiveren",
      activate: "Activeren",
      deactivated_toast: "Account gedeactiveerd: alle sessies zijn beëindigd",
      activated_toast: "Account geactiveerd",
    },
    moderation: {
      lead: "Meldingen van studenten en van de privacyfilter. Doel: binnen 48 uur afgehandeld. Elke actie komt in de auditlog en kan je heropenen.",
      tabsLabel: "Onderdelen",
      tabs: { open: "Open", resolved: "Afgehandeld", review: "Te controleren", audit: "Auditlog" },
      queueLabel: "Wachtrij",
      keyboardHint: "↑ en ↓ om door de wachtrij te gaan.",
      rulesTitle: "Spelregels voor moderatie",
      rules: [
        { title: "Geen persoonsnamen.", text: "Ook geen mailadressen of gsm-nummers van contactpersonen." },
        { title: "Zakelijk, geen oordelen.", text: "Een reden helpt, een mening niet." },
        { title: "Bij twijfel: verbergen en navragen.", text: "Terugzetten kan altijd." },
      ],
      empty: { open: "Niets te modereren. Mooi zo.", resolved: "Nog niets afgehandeld.", review: "Alle bedrijven zijn gecontroleerd." },
      loadError: "De wachtrij kon niet geladen worden.",
      pick: "Kies een melding in de wachtrij.",
      reasons: {
        persoonsgegevens: "Persoonsgegevens",
        onjuist: "Onjuist",
        toon: "Ongepaste toon",
        dubbel: "Dubbel bedrijf",
        spam: "Spam",
        account: "Account",
      },
      targets: { report: "Melding", company: "Bedrijf", user: "Account" },
      automatic: "Automatisch (privacyfilter)",
      flaggedBy: (who: string) => `Gemeld door ${who}`,
      flaggedOn: (date: string) => `op ${date}`,
      comment: "Toelichting van de melder",
      commentAutomatic: "Wat de filter vond",
      author: "Auteur",
      anonymous: "meldde anoniem",
      noAuthor: "Account verwijderd",
      hidden: (reason: string) => `Verborgen: ${reason}`,
      tipText: "Tip",
      reasonNote: "Toelichting bij de reden",
      noText: "Geen vrije tekst.",
      openCompany: "Open de fiche",
      kbo: (kbo: string | null) => (kbo ? `KBO ${kbo}` : "zonder KBO-nummer"),
      needsReview: "te controleren",
      userStatus: { active: "Actief", deactivated: "Geblokkeerd" },
      actionsTitle: "Wat doe je ermee?",
      actions: {
        behouden: "Behouden",
        aangepast: "Tekst aangepast",
        verborgen: "Verborgen",
        samengevoegd: "Samengevoegd",
        geblokkeerd: "Geblokkeerd",
      },
      keep: "Behouden",
      redact: "Naam verwijderen",
      hide: "Verbergen",
      merge: "Samenvoegen met…",
      blockAuthor: "Auteur blokkeren",
      blockUser: "Account blokkeren",
      reopen: "Heropenen",
      resolvedAs: (action: string, date: string) => `${action} op ${date}`,
      redactTitle: "Naam of contactgegevens verwijderen",
      redactLead: "Pas de tekst aan en controleer de voorvertoning. De oude tekst wordt overschreven.",
      preview: "Voorvertoning",
      before: "Nu",
      after: "Na aanpassen",
      save: "Aanpassing opslaan",
      cancel: "Annuleren",
      hideReason: "Waarom verberg je deze melding?",
      hideReasonHelp: "Enkel voor moderatoren. De melding telt niet meer voor de status.",
      hideConfirm: "Verbergen",
      toasts: {
        behouden: "Melding behouden",
        aangepast: "Tekst aangepast",
        verborgen: "Melding verborgen; de status is herberekend",
        samengevoegd: (into: string) => `Samengevoegd met ${into}`,
        geblokkeerd: "Account geblokkeerd: alle sessies zijn beëindigd",
        reopened: "Melding heropend",
        approved: (name: string) => `${name} goedgekeurd`,
      },
      mergeTitle: (name: string) => `${name} samenvoegen`,
      mergeLead: "Kies het bedrijf dat blijft bestaan.",
      mergeSearch: "Bedrijf dat blijft",
      mergeNone: "Geen bedrijf gevonden.",
      mergeSearching: "Zoeken…",
      mergeExplain: (source: string, into: string) =>
        `Meldingen, claims, volgers, wachtlijst en herinneringen van ${source} gaan naar ${into}. ${source} verdwijnt daarna. Dit kan je niet ongedaan maken.`,
      mergeConfirm: "Samenvoegen",
      reviewLead: "Bedrijven zonder KBO-nummer en IT-beheerders uit \"Volg de lijn\". Vul aan wat ontbreekt en keur goed.",
      reviewListLabel: "Te controleren bedrijven",
      reviewName: "Naam",
      reviewMunicipality: "Gemeente",
      reviewMunicipalityHelp: (current: string) => `Nu: ${current}. Leeg laten = niet wijzigen.`,
      reviewKbo: "KBO-nummer",
      approve: "Goedkeuren",
      auditLabel: "Auditlog",
      auditEmpty: "Nog geen acties.",
      auditActions: {
        "flag.behouden": "behield een melding",
        "flag.aangepast": "paste een tekst aan",
        "flag.verborgen": "verborg een melding",
        "flag.samengevoegd": "voegde een dubbel samen",
        "flag.geblokkeerd": "blokkeerde een account",
        "flag.reopen": "heropende een melding",
        "company.merge": "voegde een bedrijf samen",
        "company.review": "controleerde een bedrijf",
        "invite_code.create": "maakte een klascode",
        "invite_code.revoke": "trok een klascode in",
        "user.lookup": "zocht een account op",
        "user.recovery_code": "maakte een herstelcode",
        "user.activate": "activeerde een account",
        "user.deactivate": "deactiveerde een account",
      } as Record<string, string>,
      auditUnknown: (action: string) => action,
      someone: "Een verwijderd account",
    },
  },
  privacy: {
    toApp: "Naar Stagevijver",
    lead: "Stagevijver bewaart zo weinig mogelijk. Hier staat in gewone taal wat we bijhouden, wie wat ziet en hoe je alles laat verwijderen.",
    deleted: "Je account is verwijderd. Je meldingen blijven anoniem staan. Bedankt om mee te vissen.",
    keepTitle: "Wat we van jou bewaren",
    keep: [
      "Je schoolmail, om in te loggen. Nooit in de logs in leesbare vorm.",
      "Je wachtwoord als onleesbare hash en/of het publieke deel van je passkey. Herstelcodes enkel als hash.",
      "Je initialen, profiel(en), startgemeente (geen adres), afstand en vervoer, en je meldingsvoorkeuren.",
      "Je meldingen, claims, gevolgde bedrijven en je plaats op wachtlijsten.",
    ],
    neverTitle: "Wat we nooit bewaren",
    never: [
      "Namen, e-mailadressen of telefoonnummers van contactpersonen bij bedrijven. Een rol mag (\"vraag naar de IT-verantwoordelijke\"), een naam niet.",
      "Het adres of de naam van de persoon achter een eenmanszaak: enkel handelsnaam en gemeente.",
      "Je cijfers, je cv of je motivatiebrieven.",
      "Trackers, advertenties of externe analytics. Lettertypes en kaarten komen niet van derden.",
    ],
    whoTitle: "Wie ziet wat",
    who: [
      "Andere studenten zien je meldingen. Meld je anoniem (standaard aan), dan zien ze je initialen niet.",
      "Moderatoren zien bij een melding wie ze maakte, ook als je anoniem meldde, zodat ze misbruik kunnen aanpakken.",
      "Statistieken en exports voor moderatoren zijn geanonimiseerd: geen namen, initialen of vrije tekst.",
    ],
    howTitle: "Hoe we persoonsgegevens tegenhouden",
    how: [
      "Een filter zoekt in vrije tekst naar namen, e-mailadressen, telefoonnummers en links, en waarschuwt je vóór je verstuurt.",
      "Verstuur je toch, dan kijkt een moderator mee en kan die de naam verwijderen of de melding verbergen.",
      "Elke actie van een moderator komt in een auditlog.",
    ],
    retentionTitle: "Hoe lang we iets bewaren",
    retention: [
      "Meldingen blijven over academiejaren heen staan (oudere als geschiedenis), want ze helpen volgende lichtingen. Enkel meldingen van dit academiejaar tellen voor de status.",
      "Sessies verlopen na 30 dagen zonder activiteit.",
      "Verwijder je je account (via Profiel), dan verdwijnen je account, wachtwoord, passkeys, herstelcodes, sessies, voorkeuren, gevolgde bedrijven en je activiteitsfeed meteen. Je meldingen blijven anoniem staan.",
    ],
    contactTitle: "Vragen of een verwijderverzoek",
    contactMail: "Mail naar",
    contactFallback: "Spreek een moderator aan of vraag het aan de stagecoördinator; die geeft je vraag door.",
    companies: "Ook een bedrijf kan vragen om een melding na te kijken of te verwijderen.",
  },
  stats: {
    lead: "Elk \"nee\" hieronder is een motivatiebrief die een volgende student niet meer hoeft te sturen.",
    periodsLabel: "Periode",
    periods: { month: "Deze maand", semester: "Dit semester", all: "Dit academiejaar" },
    lower: { month: "deze maand", semester: "dit semester", all: "dit academiejaar" },
    range: (start: string, end: string) => `${start} – ${end}`,
    export: "Exporteer CSV",
    exportHelp: "Geanonimiseerd: zonder studenten, initialen of vrije tekst.",
    active: (active: number, total: number) => `${active} van ${total}`,
    activeText: (period: string) => `studenten deelden ${period} wat ze hoorden.`,
    kpis: {
      companies: "Bedrijven in de lijst",
      companiesDelta: (n: number, period: string) => `+${n} nieuw ${period}`,
      reports: "Meldingen",
      nee: "Nee's gedeeld",
      neeInfo: "Meldingen met uitkomst nee: brieven die een volgende student niet meer hoeft te sturen.",
      placed: "Studenten met een stageplaats",
      placedOf: (total: number) => `van ${total}`,
    },
    statusTitle: "Status van de bedrijven",
    statusSub: { month: "met een melding deze maand", semester: "met een melding dit semester", all: "alle bedrijven, huidige status" },
    statusAria: (parts: string) => `Verdeling: ${parts}`,
    weeksTitle: "Meldingen per week",
    weeksSub: "Weken vanaf maandag. Buiten de gekozen periode lichter.",
    weekAria: (day: string, n: number, inPeriod: boolean) =>
      `Week van ${day}: ${n} ${n === 1 ? "melding" : "meldingen"}${inPeriod ? "" : ", buiten de gekozen periode"}`,
    weekTip: (n: number) => `${n} ${n === 1 ? "melding" : "meldingen"}`,
    reasonsTitle: "Waarom zeggen bedrijven nee?",
    reasonsSub: (n: number) => `${n} keer nee, reden zoals de student ze meldde`,
    msp: (n: number) => `${n} ${n === 1 ? "IT-beheerder" : "IT-beheerders"} automatisch toegevoegd als tip`,
    placesTitle: "Per gemeente",
    placesSub: "Klik op een kolomkop om te sorteren.",
    columns: { name: "Gemeente", companies: "Bedrijven", kans: "Nog plaats", nee: "Nee", response_days: "Gem. reactietijd" },
    days: (d: number) => `${d.toLocaleString("nl-BE", { maximumFractionDigits: 1 })} d`,
    noResponse: "—",
    responseHelp: "Reactietijd: dagen tussen solliciteren of claimen en het eerste antwoord van het bedrijf.",
    gapsTitle: "Witte vlekken",
    gapsLead: "Gemeenten waar studenten wonen, maar bijna nog niemand gevist heeft.",
    gapCompanies: (n: number) => (n === 1 ? "1 bedrijf" : `${n} bedrijven`),
    gapsEmpty: "Geen witte vlekken: in elke woonplaats staan al bedrijven.",
    addTip: "Bedrijf toevoegen",
    empty: "Nog geen meldingen in deze periode.",
    loadError: "De statistieken konden niet geladen worden.",
    footnote: "Geanonimiseerd. Cijfers bevatten geen persoonsgegevens en tellen enkel zichtbare meldingen van het huidige academiejaar.",
  },
  notFound: {
    title: "Pagina niet gevonden",
    body: "Deze pagina bestaat niet (meer).",
    back: "Naar het overzicht",
  },
  errors: {
    flag_resolved: "Deze melding is al afgehandeld. Heropen ze eerst.",
    flag_not_found: "Deze melding aan de moderatie bestaat niet.",
    hidden_reason_required: "Geef een reden om te verbergen.",
    redacted_text_required: "Geef de aangepaste tekst.",
    merge_conflict_claims: "Beide bedrijven hebben een lopende claim; sluit er eerst een af.",
    merge_into_self: "Een bedrijf kan niet met zichzelf samengaan.",
    merge_into_required: "Kies het bedrijf om mee samen te voegen.",
    action_not_allowed: "Deze actie past niet bij dit soort melding.",
    network_error: "Geen verbinding met Stagevijver. Controleer je internet en probeer opnieuw.",
    unknown_error: "Er ging iets mis. Probeer het later opnieuw.",
    internal_error: "Er ging iets mis. Probeer het later opnieuw.",
    not_found: "Niet gevonden.",
    validation_error: "Controleer de ingevulde gegevens.",
    rate_limited: "Even geduld: te veel pogingen. Probeer het straks opnieuw.",
    not_authenticated: "Log eerst in.",
    invalid_domain: "Gebruik je schoolmail (@student.thomasmore.be).",
    invalid_email: "Dat is geen geldig e-mailadres.",
    invalid_invite_code: "Deze klascode is niet (meer) geldig.",
    email_taken: "Dit adres heeft al een account. Inloggen?",
    weak_password: "Kies een sterk wachtwoord van minstens 12 tekens dat niet te raden is.",
    invalid_credentials: "E-mail of wachtwoord klopt niet.",
    invalid_recovery_code: "E-mail of herstelcode klopt niet.",
    challenge_expired: "Dat duurde te lang. Probeer opnieuw.",
    passkey_invalid: "Deze passkey kon niet geverifieerd worden.",
    passkey_exists: "Deze passkey is al geregistreerd.",
    passkey_cancelled: "Passkey geannuleerd. Probeer opnieuw of kies een wachtwoord.",
    forbidden: "Enkel voor moderatoren.",
    expires_in_past: "De vervaldatum ligt in het verleden.",
    invite_code_not_found: "Deze klascode bestaat niet (meer).",
    user_not_found: "Geen account met dit adres.",
    cannot_change_self: "Je kan je eigen account niet (de)activeren.",
    unknown_municipality: "Kies een gemeente uit de lijst.",
    company_not_found: "Dit bedrijf bestaat niet (meer).",
    reason_required: "Kies een reden voor de nee.",
    reason_note_required: "Licht de reden kort toe.",
    field_not_allowed: "Er staat een veld in dat niet bij deze uitkomst hoort.",
    invalid_spots: "Controleer het aantal plaatsen.",
    contains_personal_data: "Je tekst lijkt persoonsgegevens te bevatten.",
    new_account_limit: "Je account is nieuw: de eerste 24 uur kan je maximaal 5 meldingen en 1 claim doen.",
    already_claimed: "Een medestudent heeft dit bedrijf al geclaimd.",
    claim_limit_reached: "Je hebt al 2 actieve claims. Sluit er eerst een af.",
    waitlist_priority: "Een medestudent van de wachtlijst heeft nog voorrang op dit bedrijf.",
    undo_expired: "Ongedaan maken kan enkel de eerste 10 minuten.",
    report_not_found: "Deze melding bestaat niet (meer).",
    claim_not_found: "Deze claim bestaat niet (meer).",
    claim_closed: "Deze claim is al afgelopen.",
    already_extended: "Je kan een claim maar één keer verlengen.",
    not_claimed: "Niemand heeft dit bedrijf geclaimd: claim het zelf.",
    own_claim: "Je hebt dit bedrijf zelf geclaimd.",
    home_municipality_required: "Kies eerst je startgemeente in je profiel om op afstand te zoeken.",
    invalid_kbo_number: "Dit is geen geldig ondernemingsnummer.",
    company_exists: "Dit bedrijf staat al in de lijst.",
    possible_duplicate: "Er staat al een bedrijf met deze naam in deze gemeente.",
  },
} as const;

export type ErrorCode = keyof typeof nl.errors;
